import { select, Selection, BaseType } from "d3-selection"
import { line, curveLinear } from "d3-shape"
import { Widget } from "./widget"
import * as Dom from "../util/dom"

export interface Node {
  x: number
  y: number
  id: number
  desc: string
  children?: Node[]
  root: boolean
  old?: boolean
  color?: string
  warning?: string
  distance?: number
  url?: string
  text: string
  clickable?: boolean
  icon: string
  type: string
  query: unknown
}

export interface Link {
  source: Node
  target: Node
}

export const bigNodeRadius = 20
export const smallNodeRadius = 8

export function createArrowPath(arrowSize: number): string {
  const arrowRatio = 0.5
  const d = [
    { x: 0, y: 0 },
    { x: 1, y: arrowRatio },
    { x: 1, y: -arrowRatio },
    { x: 0, y: 0 },
  ]
  const lineFunction = line<{ x: number; y: number }>()
    .x(function (d) {
      return arrowSize * d.x
    })
    .y(function (d) {
      return arrowSize * d.y
    })
    .curve(curveLinear)
  return lineFunction(d) as string
}

// in SVG, texts don't have background. Thus, we add a "rect" element to serve as the background
// this function keeps the position and width of text and background in sync
export function syncTextBackground<
  GElement extends BaseType,
  Datum,
  PElement extends BaseType,
>(
  elements: Selection<GElement, Datum, PElement, unknown>,
  textSelector: string,
  bgSelector: string,
  marginX: number,
  marginY: number,
  maxWidth: number,
  cornerRadius: number = 0
) {
  elements.each(function () {
    const parent = select(this)
    const text = parent.select<SVGTextElement>(textSelector).node()
    const bg = parent.select<SVGRectElement>(bgSelector).node()
    if (text && bg && text.textContent) {
      const bbox = text.getBBox()
      const dx = Number(parent.attr("data-dx"))
      if (dx != 0) {
        const oldX = bbox.x
        const minX = -dx + marginX
        const maxX = maxWidth - marginX - bbox.width - dx
        if (oldX < minX) {
          bbox.x = minX
          select(text).attr("x", minX - oldX)
        } else if (oldX > maxX) {
          bbox.x = maxX
          select(text).attr("x", maxX - oldX)
        }
      }
      select(bg)
        .attr("width", bbox.width + marginX * 2)
        .attr("height", bbox.height + marginY * 2)
        .attr("x", bbox.x - marginX)
        .attr("y", bbox.y - marginY)
        .attr("rx", cornerRadius)
        .attr("ry", cornerRadius)
    }
  })
}

function moveToFront(elements: Element[], handleLinksSeparately = false) {
  if (Dom.isIE()) {
    return
  }

  // in SVG, the last children are always in front
  // thus, we have to move the elements to the end in their parent's children list
  // this is only possible by re-appending them.
  elements.reverse()
  const uniqueNodes: Element[] = []
  const uniqueLinks: Element[] = []
  for (const element of elements) {
    const isLink = handleLinksSeparately && element.classList.contains("link")
    const elements = isLink ? uniqueLinks : uniqueNodes
    if (!elements.includes(element)) {
      elements.push(element)
    }
  }
  uniqueLinks.reverse()
  uniqueNodes.reverse()

  if (shouldMoveElements(uniqueLinks)) {
    for (const element of uniqueLinks) {
      element.parentNode?.appendChild(element)
    }
  }
  if (shouldMoveElements(uniqueNodes)) {
    for (const element of uniqueNodes) {
      element.parentNode?.appendChild(element)
    }
  }
}

// move only if not already there to avoid flickering
function shouldMoveElements(elements: Element[]) {
  const firstElement = elements[0]
  if (!firstElement) {
    return false
  }

  const size = elements.length
  const children = firstElement.parentNode?.childNodes ?? []
  for (let i = 0; i < size; ++i) {
    if (children[children.length - size + i] != elements[i]) {
      return true
    }
  }
  return false
}

export function drawNodes<
  GElement extends BaseType,
  Datum,
  PElement extends BaseType,
>(
  widget: Widget,
  svg: Selection<GElement, Datum, PElement, unknown>,
  nodes: Node[],
  rootId: number | undefined,
  options: { denseLeaves?: boolean; separateLinksContainer?: boolean } = {}
) {
  const maxWidth = widget.container.getBoundingClientRect().width

  function selectNode(id: number) {
    return svg.select<Element>(".node[data-id='" + id + "']")
  }

  function selectRootNode() {
    return svg.select<Element>("[data-root]")
  }

  let restoreDefaultOrder: number /* timeout handler */

  function highlight(node: Node) {
    const nodeId = node.id
    const highlightMode = nodeId == rootId ? "root" : "node"

    // highlight node
    const nodeEl = selectNode(nodeId)
      .attr("data-highlight", highlightMode)
      .classed("hovered", true)
    nodeEl.select<Element>(".text").text(node.desc)
    syncTextBackground(
      nodeEl,
      ".text",
      ".text-bg",
      options.denseLeaves ? 2 : 5,
      2,
      maxWidth
    )

    // highlight links
    const linkEls = svg
      .selectAll<Element, Link>(
        "[data-source-id='" + nodeId + "'],[data-target-id='" + nodeId + "']"
      )
      .attr("data-highlight", highlightMode)
    syncTextBackground(linkEls, ".desc", ".desc-bg", 3, 2, maxWidth)

    // bring highlighted stuff to front
    window.clearTimeout(restoreDefaultOrder)
    const toBeMoved: Element[] = []
    linkEls.each(function (link) {
      toBeMoved.push(this)
      function moveOtherEndToFront(id: number) {
        if (id != nodeId) {
          toBeMoved.push(selectNode(id).node() as Element)
        }
      }
      moveOtherEndToFront(link.source.id)
      moveOtherEndToFront(link.target.id)
    })
    toBeMoved.push(nodeEl.node() as Element)
    moveToFront(toBeMoved, options.separateLinksContainer)
  } // end highlight

  function arrangeInDefaultOrder() {
    const toBeMoved: Element[] = []
    svg.select<Element>(".node").each(function () {
      toBeMoved.push(this)
    })
    toBeMoved.push(selectRootNode().node() as Element)
    moveToFront(toBeMoved)
  }

  function unhighlight(node: Node) {
    svg
      .selectAll("[data-highlight]")
      .attr("data-highlight", "false")
      .classed("hovered", false)
    const nodeEl = svg.select(".node[data-id='" + node.id + "']")
    nodeEl.select(".text").text(node.text).attr("x", 0)

    restoreDefaultOrder = window.setTimeout(arrangeInDefaultOrder, 1000)
  }

  let d3NodesContainer = svg.select<SVGGElement>("g.nodes")

  if (d3NodesContainer.empty()) {
    d3NodesContainer = svg.append("g").attr("class", "nodes")
  }

  d3NodesContainer.style("pointer-events", "all")
  const d3Nodes = d3NodesContainer.selectAll(".node").data(nodes)

  const clickable = (node: Node) => node.clickable || !node.root
  // I think all code related to d3NewNodes  can be deleted safely
  // because the rendering is now done on the server
  const d3NewNodes = d3Nodes
    .enter()
    .append<SVGAElement>("svg:a")
    .attr("class", "node")
    .attr("data-leaf", (node) => !node.children || node.children.length == 0)
    .attr("data-id", (node) => node.id)
    .attr("data-root", (node) => node.root)
    .attr("data-old", (node) => node.old ?? null)
    .attr("data-warning", (node) => node.warning ?? null)
    .attr("data-color", (node) => node.color ?? null)
    .attr("data-d", (node) => node.distance ?? null)
    .attr("xlink:href", function (node) {
      return node.url || "" // clickable(node) ? widget.getLinkifier()(this) : null
    })
    .attr("data-clickable", (node) =>
      clickable(node) && widget.getClickHandler(node.type) ? true : null
    )
    .on("click", function (event: Event, node) {
      if (clickable(node)) {
        if (widget.invokeClickHandler(node.query, node.type)) {
          event.preventDefault()
        }
      }
    })

  Dom.onEvent(
    "mouseover",
    d3NodesContainer.node() as Element,
    d3NewNodes,
    highlight
  )
  Dom.onEvent(
    "mouseout",
    d3NodesContainer.node() as Element,
    d3NewNodes,
    unhighlight
  )

  d3NewNodes.append("circle").attr("r", bigNodeRadius)

  d3NewNodes
    .append("text")
    .attr("class", "icon")
    .text((node) => node.icon)

  d3NewNodes.append("rect").attr("class", "text-bg")

  d3NewNodes
    .append("text")
    .attr("class", "text")
    .text((node) => node.text)

  d3NewNodes
    .filter("[data-warning]")
    .append("circle")
    .attr("r", 7)
    .attr("data-warn", true)
    .attr("transform", "translate(20, -20)")
  d3NewNodes
    .filter("[data-warning]")
    .append("text")
    .attr("r", 8)
    .attr("class", "icon")
    .text("\uf071")
    .attr("data-warn", true)
    .append("title")
    .text((node) => node.warning ?? "")

  arrangeInDefaultOrder()

  return d3NewNodes
}

export function enrichNodes<
  GElement extends BaseType,
  Datum,
  PElement extends BaseType,
>(
  widget: Widget,
  svg: Selection<GElement, Datum, PElement, unknown>,
  //nodes: Node[]
  //rootId: number | undefined,
  denseLeaves: boolean
) {
  const maxWidth = widget.container.getBoundingClientRect().width
  const d3NodesContainer = svg.select<SVGGElement>("g.nodes")
  const highlightContainer = svg
    .append("g")
    .attr("class", "highlight-container")
    .attr("style", "pointer-events: none;")

  function selectNode(id: string) {
    return svg.select<Element>(".node[data-id='" + id + "']")
  }

  function selectRootNode() {
    return svg.select<Element>("[data-root]")
  }

  let restoreDefaultOrder: number /* timeout handler */

  function highlight(node: Element) {
    const nodeId = node.getAttribute("data-id")
    const highlightMode =
      node.getAttribute("data-root") == "true" ? "root" : "node"

    // highlight node
    select(node).attr("data-highlight", highlightMode).classed("hovered", true)
    var textNode = node.querySelector(".text")
    if (textNode) {
      textNode.textContent = node.getAttribute("data-description")
    }

    syncTextBackground(
      select(node),
      ".text",
      ".text-bg",
      denseLeaves ? 2 : 5,
      2,
      maxWidth
    )

    // highlight links
    const linkEls = d3NodesContainer
      .selectAll<Element, Link>(
        ".link[data-source-id='" +
          nodeId +
          "'],.link[data-target-id='" +
          nodeId +
          "']"
      )
      .attr("data-highlight", highlightMode)
    syncTextBackground(linkEls, ".desc", ".desc-bg", 3, 2, maxWidth)

    // bring highlighted stuff to front
    window.clearTimeout(restoreDefaultOrder)
    const toBeMoved: Element[] = []
    linkEls.each(function () {
      var linkEl = this
      toBeMoved.push(linkEl)
      function moveOtherEndToFront(id: string) {
        if (id != nodeId) {
          toBeMoved.push(selectNode(id).node() as Element)
        }
      }
      moveOtherEndToFront(linkEl.getAttribute("data-source-id") || "")
      moveOtherEndToFront(linkEl.getAttribute("data-target-id") || "")
    })
    toBeMoved.push(node)
    moveToFront(toBeMoved)
  }

  function highlightLink(link: Element) {
    // Move clone of link element to highlight container that is always in front of all nodes & links
    const linkClone = select(link).clone(true)
    linkClone
      .attr("data-highlight", "link")
      .selectAll(":not(.desc, .desc-bg)")
      .remove()

    const linkCloneNode = linkClone.node()
    if (linkCloneNode) {
      highlightContainer.html(null)
      highlightContainer.node()?.append(linkCloneNode)
    }
  }

  function unhighlightAll() {
    svg
      .selectAll("[data-highlight]")
      .attr("data-highlight", "false")
      .classed("hovered", false)
  }

  function arrangeInDefaultOrder() {
    const toBeMoved: Element[] = []
    svg.select<Element>(".node").each(function () {
      toBeMoved.push(this)
    })
    toBeMoved.push(selectRootNode().node() as Element)
    moveToFront(toBeMoved)
  }

  function unhighlight(node: Element) {
    unhighlightAll()
    const nodeId = node.getAttribute("data-id")
    svg
      .select(".node[data-id='" + nodeId + "']")
      .select(".text")
      .text(node.getAttribute("data-text") || "")
      .attr("x", 0)

    restoreDefaultOrder = window.setTimeout(arrangeInDefaultOrder, 1000)
  }

  d3NodesContainer.selectAll(".node").each(function () {
    const el = this as Element
    const root = el.getAttribute("data-root") == "true"
    const linkify = widget.getLinkifier()
    if (!root) {
      const type = el.getAttribute("data-type") || ""
      const query = el.getAttribute("data-query") || ""
      select(el)
        .attr("xlink:href", linkify(el))
        .attr("data-clickable", widget.getClickHandler(type) ? "true" : "false")
        .on("click", function (event: Event) {
          if (widget.invokeClickHandler(query, type)) {
            event.preventDefault()
          }
        })
    }
  })

  d3NodesContainer
    .selectAll<Element, unknown>(".node")
    .on("mouseover", function () {
      highlight(this)
    })
    .on("mouseout", function () {
      unhighlight(this)
    })

  d3NodesContainer
    .selectAll<Element, unknown>(".link")
    .on("mouseover", function () {
      highlightLink(this)
    })
    .on("mouseout", function () {
      unhighlightAll()
    })

  arrangeInDefaultOrder()
}
